import { useState } from "react";
import { Box, Button, TextField, Typography, Card, CardContent, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../../config";

const Login = ({ setAuthToken }) => {
  const [loading, setLoading] = useState(false);

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Identifiants invalides");
      }

      const { authToken, role, username } = data;

      sessionStorage.setItem("authToken", authToken);
      sessionStorage.setItem("username", username || "Admin");
      sessionStorage.setItem("role", typeof role === "string" ? role : "superadmin");

      setAuthToken(authToken);
      toast.success(`Bienvenue, ${username} !`);
    } catch (error) {
      toast.error(error.message || "Échec de connexion. Vérifiez vos identifiants.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100vw",
        height: "100vh",
        backgroundColor: "#141b2d",
        p: 2
      }}
    >
      <Card
        sx={{
          backgroundColor: "#1F2A40",
          width: "100%",
          maxWidth: "420px",
          p: 3,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
          borderRadius: "8px",
          border: "1px solid rgba(255, 255, 255, 0.08)"
        }}
      >
        <CardContent sx={{ textAlign: "center", p: 0 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              backgroundColor: "rgba(76, 206, 172, 0.15)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "0 auto 15px auto"
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 32, color: "#4cceac" }} />
          </Box>

          <Typography variant="h3" fontWeight="bold" color="#fff" mb="5px">
            LinkerVTC
          </Typography>
          <Typography variant="body2" color="#a3a3a3" mb="30px">
            Portail d'Administration Sécurisé
          </Typography>

          <Formik
            onSubmit={handleFormSubmit}
            initialValues={{ identifier: "", password: "" }}
            validationSchema={checkoutSchema}
          >
            {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <Box display="flex" flexDirection="column" gap={2.5}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Identifiant ou Adresse Email"
                    name="identifier"
                    value={values.identifier}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.identifier && !!errors.identifier}
                    helperText={touched.identifier && errors.identifier}
                  />

                  <TextField
                    fullWidth
                    variant="outlined"
                    type="password"
                    label="Mot de passe"
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      mt: 1,
                      p: "12px",
                      backgroundColor: "#4cceac",
                      color: "#141b2d",
                      fontWeight: "bold",
                      fontSize: "15px",
                      "&:hover": { backgroundColor: "#3da58a" }
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Se Connecter"}
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Box>
  );
};

const checkoutSchema = yup.object().shape({
  identifier: yup.string().required("Identifiant requis"),
  password: yup.string().required("Mot de passe requis"),
});

export default Login;
